import React, { useState, useEffect } from 'react';
import Header from '../../layouts/Header';
import { useNavigate } from 'react-router-dom';
import {
    Row, Col, Table, Button, Form, Modal, InputGroup
} from 'react-bootstrap';
import {
    PlusLg, PencilSquare, Trash3, Search, CalendarEvent,
    ChevronLeft, ChevronRight, ArrowLeft, Clock, Funnel, Tag, ShopWindow
} from 'react-bootstrap-icons';
import { AlertCircle, Loader2 } from 'lucide-react';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import axiosInstance from '../../api/axios';
import { toast } from 'react-toastify';
import OutletSelect from '../../components/OutletSelect'; // adjust path as needed

// ─── Constants ────────────────────────────────────────────────────────────────

const EVENT_NAME_OPTIONS = [
    { value: 'CATEGORY_ACTION', label: 'Category Action' },
    { value: 'PRODUCT_ACTION', label: 'Product Action' },
    // { value: 'ADVERTISEMENT_ACTION', label: 'Advertisement Action' },
    { value: 'POPUP_ADVERTISMENT_ACTION', label: 'Popup Advertisement Action' },
    { value: 'OUTLET_ACTION', label: 'Outlet Action' },
];

const EVENT_TYPE_COLORS = {
    CATEGORY_ACTION: 'primary',
    PRODUCT_ACTION: 'success',
    ADVERTISEMENT_ACTION: 'info',
    POPUP_ADVERTISMENT_ACTION: 'warning',
    OUTLET_ACTION: 'secondary',
};

const APPLICABLE_FIELD_MAP = {
    CATEGORY_ACTION: 'applicableCategory',
    PRODUCT_ACTION: 'applicableProduct',
    ADVERTISEMENT_ACTION: 'applicableAdvertisement',
    POPUP_ADVERTISMENT_ACTION: 'applicablePopupAdvertisment',
    OUTLET_ACTION: 'applicableOutlet',
};

const defaultForm = {
    eventDate: null,   // Date object for react-datepicker
    eventTime: '',
    eventName: 'CATEGORY_ACTION',
    eventStatus: 'ACTIVE',
    applicableCategory: null,   // { value, label } for react-select
    applicableProduct: null,
    applicableAdvertisement: null,
    applicablePopupAdvertisment: null,
    applicableOutlet: null,
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const extractId = (val) => {
    if (!val) return '';
    if (typeof val === 'string') return val;
    if (typeof val === 'object') return val._id || '';
    return '';
};

const extractName = (val) => {
    if (!val) return '—';
    if (typeof val === 'string') return val;
    if (typeof val === 'object') return val.name || val.title || val._id || '—';
    return '—';
};

const getApplicableName = (ev) => {
    const val =
        ev.applicableCategory ||
        ev.applicableProduct ||
        ev.applicableAdvertisement ||
        ev.applicablePopupAdvertisment ||
        ev.applicableOutlet ||
        null;
    return extractName(val);
};

const safe = (val, fallback = '—') => {
    if (val === null || val === undefined) return fallback;
    if (typeof val === 'object') return val.name || val._id || fallback;
    return String(val);
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatDateLocal = (date) => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

// react-select styling tuned to match the dense, bordered inputs used elsewhere
const selectStyles = {
    control: (base, state) => ({
        ...base,
        fontSize: '13px',
        minHeight: '38px',
        borderRadius: '6px',
        borderColor: state.isFocused ? '#4f46e5' : '#e1e4ea',
        boxShadow: state.isFocused ? '0 0 0 3px rgba(79,70,229,0.12)' : 'none',
        '&:hover': { borderColor: '#4f46e5' },
    }),
    option: (base, state) => ({
        ...base,
        fontSize: '13px',
        backgroundColor: state.isSelected ? '#4f46e5' : state.isFocused ? '#eef0ff' : '#fff',
        color: state.isSelected ? '#fff' : '#1f2937',
    }),
    placeholder: (base) => ({ ...base, fontSize: '13px', color: '#9aa1ac' }),
    singleValue: (base) => ({ ...base, fontSize: '13px' }),
    menuPortal: (base) => ({
        ...base,
        zIndex: 9999,
    }),
};

// ─── Component ────────────────────────────────────────────────────────────────

const AutomationEvents = () => {
    const currentSkin = localStorage.getItem('skin-mode') ? 'dark' : '';
    const [skin, setSkin] = useState(currentSkin);
    const accessToken = localStorage.getItem('accessToken');
    const navigate = useNavigate();
    const isDark = skin === 'dark';

    // list data
    const [events, setEvents] = useState([]);
    const [pagination, setPagination] = useState({ totalRecords: 0, totalPages: 1, currentPage: 1, limit: 10 });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // dropdown data
    const [categoryOptions, setCategoryOptions] = useState([]);
    const [productOptions, setProductOptions] = useState([]);
    const [adOptions, setAdOptions] = useState([]);
    const [popupAdOptions, setPopupAdOptions] = useState([]);

    // form / modal
    const [showModal, setShowModal] = useState(false);
    // const [isEditing, setIsEditing]   = useState(false);  // EDIT: commented out
    // const [editId, setEditId]         = useState(null);    // EDIT: commented out
    const [form, setForm] = useState(defaultForm);
    const [formError, setFormError] = useState('');
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(null);

    // delete confirmation modal
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteTargetId, setDeleteTargetId] = useState(null);

    // filters
    const [search, setSearch] = useState('');
    const [filterType, setFilterType] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [page, setPage] = useState(1);

    // ─── Effects ────────────────────────────────────────────────────────────
    // NOTE: filterType / filterStatus are now sent to the API, so the fetch
    // must re-run whenever either of them changes (in addition to page changes).
    useEffect(() => { fetchEvents(page); }, [page, filterType, filterStatus]);
    useEffect(() => {
        fetchCategoryOptions();
        fetchProductOptions();
        fetchAdOptions();
        fetchPopupAdOptions();
    }, []);

    // ─── Fetch events ────────────────────────────────────────────────────────
    const fetchEvents = async (pageNum = 1) => {
        setLoading(true);
        setError('');
        try {
            const params = { page: pageNum, limit: 10 };
            // EDIT: eventName / eventStatus filtering moved to API level
            if (filterType) params.eventName = filterType;
            if (filterStatus) params.eventStatus = filterStatus;

            const res = await axiosInstance.get('automation-event', { params });
            const payload = res.data.data;
            setEvents(payload.dbResponse || []);
            setPagination(payload.pagination || { totalRecords: 0, totalPages: 1, currentPage: 1, limit: 10 });
        } catch (err) {
            setError(err?.response?.data?.message || err.message || 'Failed to fetch events');
        } finally {
            setLoading(false);
        }
    };

    // ─── Fetch dropdown options ───────────────────────────────────────────────
    const fetchCategoryOptions = async () => {
        try {
            const res = await axiosInstance.get('masters/category/get', {
                headers: { Authorization: `Bearer ${accessToken}` },
            });
            const list = res?.data?.data?.categories || [];
            setCategoryOptions(list.map(c => ({ value: c._id, label: c.name })));
        } catch (e) { console.error('Category fetch error', e); }
    };

    const fetchProductOptions = async () => {
        try {
            const res = await axiosInstance.get('product', {
                headers: { Authorization: `Bearer ${accessToken}` },
            });
            const list = res?.data?.data?.products || [];
            setProductOptions(list.map(p => ({ value: p._id, label: p.name })));
        } catch (e) { console.error('Product fetch error', e); }
    };

    const fetchAdOptions = async () => {
        try {
            const res = await axiosInstance.get('advertisement', {
                headers: { Authorization: `Bearer ${accessToken}` },
            });
            const list = res?.data?.data?.advertisements?.clickableAdvertisements || [];
            setAdOptions(list.map(a => ({ value: a._id, label: a.title || a.name || a._id })));
        } catch (e) { console.error('Advertisement fetch error', e); }
    };

    const fetchPopupAdOptions = async () => {
        try {
            const res = await axiosInstance.get('in-app-advertisement', {
                headers: { Authorization: `Bearer ${accessToken}` },
            });
            const list = res?.data?.data?.inAppAdvertisement || [];
            setPopupAdOptions(list.map(a => ({ value: a._id, label: a.title || a.name || a._id })));
        } catch (e) { console.error('Popup Ad fetch error', e); }
    };

    // ─── Modal helpers ────────────────────────────────────────────────────────
    const openAddModal = () => {
        // setIsEditing(false);  // EDIT: commented out
        // setEditId(null);      // EDIT: commented out
        setForm(defaultForm);
        setFormError('');
        setShowModal(true);
    };

    // ─── EDIT: openEditModal commented out ────────────────────────────────────
    // const openEditModal = (ev) => {
    //     setIsEditing(true);
    //     setEditId(ev._id);
    //
    //     const toOption = (val, fallbackLabel) => {
    //         if (!val) return null;
    //         if (typeof val === 'object') return { value: val._id, label: val.name || val.title || val._id };
    //         return { value: val, label: fallbackLabel || val };
    //     };
    //
    //     setForm({
    //         eventDate:                    ev.eventDate ? new Date(ev.eventDate) : null,
    //         eventTime:                    ev.eventTime || '',
    //         eventName:                    ev.eventName || 'CATEGORY_ACTION',
    //         eventStatus:                  ev.eventStatus || 'ACTIVE',
    //         applicableCategory:           toOption(ev.applicableCategory),
    //         applicableProduct:            toOption(ev.applicableProduct),
    //         applicableAdvertisement:      toOption(ev.applicableAdvertisement),
    //         applicablePopupAdvertisment: toOption(ev.applicablePopupAdvertisment),
    //         applicableOutlet:             toOption(ev.applicableOutlet),
    //     });
    //     setFormError('');
    //     setShowModal(true);
    // };

    // ─── Form change handlers ─────────────────────────────────────────────────
    // const handleFormChange = (e) => {
    //     const { name, value } = e.target;
    //     setForm(prev => ({ ...prev, [name]: value }));
    // };

    const handleFormChange = (e) => {
        const { name, value } = e.target;

        if (name === 'eventName') {
            setForm(prev => ({
                ...prev,
                eventName: value,
                applicableCategory: null,
                applicableProduct: null,
                applicableAdvertisement: null,
                applicablePopupAdvertisment: null,
                applicableOutlet: null,
            }));
            return;
        }

        setForm(prev => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSelectChange = (fieldName, option) => {
        setForm(prev => ({ ...prev, [fieldName]: option }));
    };

    // ─── Build payload ────────────────────────────────────────────────────────
    const buildPayload = () => {
        const applicableKey = APPLICABLE_FIELD_MAP[form.eventName];
        const selectValue = form[applicableKey];

        const applicableId = selectValue?.value || '';

        if (!applicableId) {
            setFormError(`Please select the applicable ${form.eventName.replace('_ACTION', '').toLowerCase()}`);
            return null;
        }

        const payload = {
            eventDate: form.eventDate
                ? formatDateLocal(form.eventDate)
                : '',
            eventName: form.eventName,
            eventStatus: form.eventStatus,
            [applicableKey]: applicableId,
        };

        if (form.eventTime) payload.eventTime = form.eventTime;

        // ─── EDIT: edit-specific payload fields commented out ─────────────────
        // if (isEditing) {
        //     payload._id         = editId;
        //     payload.eventStatus = form.eventStatus;
        // }

        return payload;
    };

    // ─── Save ─────────────────────────────────────────────────────────────────
    const handleSave = async () => {
        setFormError('');
        if (!form.eventDate) { setFormError('Event date is required'); return; }
        const payload = buildPayload();
        if (!payload) return;

        setSaving(true);
        try {
            // ─── EDIT: conditional put/post simplified to post only ────────────
            // if (isEditing) {
            //     await axiosInstance.put('automation-event', payload);
            // } else {
            //     await axiosInstance.post('automation-event', payload);
            // }
            await axiosInstance.post('automation-event', payload);

            toast.success('Event created successfully', {
                position: 'top-right', autoClose: 3000, theme: 'light',
            });
            setShowModal(false);
            fetchEvents(page);
        } catch (error) {
            // setFormError(err?.response?.data?.message || err.message || 'Save failed');
            console.log("Error in aDDINF EVENT :", error);

            let errorMessage = '';
            if (error.response?.status === 406) {
                errorMessage = error?.response?.data?.error || error?.response?.data?.error?.message
            }
            else if (error.response?.status === 412) {
                errorMessage = error?.response?.data?.data?.message;
            } else if (error.response?.status === 500) {
                errorMessage = error?.response?.data?.error
            } else if (error?.response?.status === 404) {
                errorMessage = error?.response?.data?.error
            }

            toast.error(errorMessage, {
                position: "top-right",
                autoClose: 3000,
                theme: "light",
            });
        } finally {
            setSaving(false);
        }
    };

    // ─── Delete ───────────────────────────────────────────────────────────────
    const openDeleteModal = (id) => {
        setDeleteTargetId(id);
        setShowDeleteModal(true);
    };

    const closeDeleteModal = () => {
        setShowDeleteModal(false);
        setDeleteTargetId(null);
    };

    const confirmDelete = async () => {
        const id = deleteTargetId;
        if (!id) return;
        setDeleting(id);
        try {
            await axiosInstance.delete('automation-event', { data: { _id: id } });
            toast.success('Event deleted successfully', { position: 'top-right', autoClose: 3000, theme: 'light' });
            setShowDeleteModal(false);
            setDeleteTargetId(null);
            if (events.length === 1 && page > 1) {
                setPage(prev => prev - 1);
            } else {
                fetchEvents(page);
            }
        } catch (err) {
            toast.error(err?.response?.data?.message || err.message || 'Delete failed', {
                position: 'top-right', autoClose: 3000, theme: 'light',
            });
        } finally {
            setDeleting(null);
        }
    };

    // ─── Filtered list ────────────────────────────────────────────────────────
    // EDIT: eventName / eventStatus filtering now happens server-side (see fetchEvents).
    // Only the free-text "search" box is still applied on the client, since the
    // API does not currently accept a text-search param.
    const filteredEvents = events.filter(ev => {
        const matchSearch = !search || ev.eventName?.toLowerCase().includes(search.toLowerCase());
        return matchSearch;
    });

    const totalActive = events.filter(e => e.eventStatus === 'ACTIVE').length;
    const totalInactive = events.filter(e => e.eventStatus === 'INACTIVE').length;

    const selectOptionsMap = {
        CATEGORY_ACTION: categoryOptions,
        PRODUCT_ACTION: productOptions,
        ADVERTISEMENT_ACTION: adOptions,
        POPUP_ADVERTISMENT_ACTION: popupAdOptions,
        OUTLET_ACTION: [],
    };

    const applicableKey = APPLICABLE_FIELD_MAP[form.eventName];

    const applicableLabel =
        form.eventName === 'CATEGORY_ACTION' ? 'Category' :
            form.eventName === 'PRODUCT_ACTION' ? 'Product' :
                form.eventName === 'ADVERTISEMENT_ACTION' ? 'Advertisement' :
                    form.eventName === 'POPUP_ADVERTISMENT_ACTION' ? 'Popup advertisement' :
                        'Outlet';

    // ─── Render ───────────────────────────────────────────────────────────────
    return (
        <React.Fragment>
            <Header onSkin={setSkin} />
            <div className={`main main-app p-3 p-lg-4 ae-page ${isDark ? 'ae-dark bg-dark text-light' : 'bg-light'}`}>

                {/* Global styles for modal portal (rendered outside .ae-page) --- */}
                <style id="ae-modal-global">{`
                    .ae-modal .modal-content {
                        border: 1px solid ${isDark ? '#2c3242' : '#e6e8ec'};
                        border-radius: 12px;
                        background: ${isDark ? '#11151f' : '#ffffff'};
                        color: ${isDark ? '#e7e9ee' : '#1f2430'};
                        overflow: hidden;
                        box-shadow: 0 20px 60px rgba(0,0,0,0.18);
                    }
                    .ae-modal .modal-header {
                        border-bottom: 1px solid ${isDark ? '#2c3242' : '#e6e8ec'};
                        background: ${isDark ? '#1b2030' : '#f8f9fb'};
                        padding: 16px 20px;
                    }
                    .ae-modal .modal-title {
                        font-size: 15px;
                        font-weight: 700;
                        color: ${isDark ? '#e7e9ee' : '#1f2430'};
                    }
                    .ae-modal .modal-header .btn-close {
                        filter: ${isDark ? 'invert(1) grayscale(1)' : 'none'};
                    }
                    .ae-modal .modal-body {
                        padding: 20px;
                        background: ${isDark ? '#11151f' : '#ffffff'};
                        color: ${isDark ? '#e7e9ee' : '#1f2430'};
                    }
                    .ae-modal .modal-footer {
                        border-top: 1px solid ${isDark ? '#2c3242' : '#e6e8ec'};
                        background: ${isDark ? '#11151f' : '#ffffff'};
                        padding: 14px 20px;
                        gap: 8px;
                    }
                    .ae-modal .modal-subtitle {
                        font-size: 12.5px;
                        color: ${isDark ? '#8a93a6' : '#6b7280'};
                        margin-top: 4px;
                        margin-bottom: 0;
                    }
                    .ae-modal .form-control,
                    .ae-modal .form-select {
                        font-size: 13px;
                        border-radius: 6px;
                        border-color: ${isDark ? '#2c3242' : '#e6e8ec'};
                        background: ${isDark ? '#11151f' : '#ffffff'};
                        color: ${isDark ? '#e7e9ee' : '#1f2430'};
                    }
                    .ae-modal .form-control:focus,
                    .ae-modal .form-select:focus {
                        border-color: #4f46e5;
                        box-shadow: 0 0 0 3px rgba(79,70,229,0.12);
                    }
                    .ae-modal .react-datepicker-wrapper { width: 100%; }
                    .ae-modal-grid {
                        display: grid;
                        grid-template-columns: 1fr 1fr;
                        gap: 16px;
                    }
                    .ae-modal-grid .ae-full-col { grid-column: 1 / -1; }
                    .ae-modal-field-label {
                        font-size: 14px;
                        font-weight: 600;
                        color: ${isDark ? '#e7e9ee' : '#1f2430'};
                        display: flex;
                        align-items: center;
                        gap: 6px;
                        margin-bottom: 6px;
                    }
                    .ae-modal-field-hint {
                        font-size: 11px;
                        color: ${isDark ? '#8a93a6' : '#6b7280'};
                        margin-top: 5px;
                        margin-bottom: 0;
                    }
                    .ae-modal-section-title {
                        font-size: 10.5px;
                        text-transform: uppercase;
                        letter-spacing: 0.09em;
                        font-weight: 700;
                        color: ${isDark ? '#8a93a6' : '#6b7280'};
                        padding-bottom: 8px;
                        margin-bottom: 14px;
                        border-bottom: 1px solid ${isDark ? '#2c3242' : '#e6e8ec'};
                    }
                    .ae-modal-error-box {
                        display: flex; gap: 8px; align-items: flex-start;
                        background: ${isDark ? '#2a1414' : '#fef2f2'};
                        border: 1px solid ${isDark ? '#4a1f1f' : '#fecaca'};
                        color: ${isDark ? '#f3a3a3' : '#b91c1c'};
                        border-radius: 6px; padding: 10px 12px; font-size: 12.5px; margin-bottom: 16px;
                    }
                    .ae-modal-status-toggle {
                        display: flex;
                        border: 1px solid ${isDark ? '#2c3242' : '#e6e8ec'};
                        border-radius: 6px;
                        overflow: hidden;
                    }
                    .ae-modal-status-btn {
                        flex: 1;
                        padding: 8px 12px;
                        font-size: 12.5px;
                        font-weight: 600;
                        border: none;
                        background: ${isDark ? '#1b2030' : '#f8f9fb'};
                        color: ${isDark ? '#8a93a6' : '#6b7280'};
                        cursor: pointer;
                        transition: all .12s ease;
                        display: flex; align-items: center; justify-content: center; gap: 6px;
                    }
                    .ae-modal-status-btn:not(:last-child) {
                        border-right: 1px solid ${isDark ? '#2c3242' : '#e6e8ec'};
                    }
                    .ae-modal-status-btn.is-active   { background: #b0ffda; color: #047857; }
                    .ae-modal-status-btn.is-inactive { background: #ffb7b7; color: #b91c1c; }
                    .ae-modal-dot { width: 6px; height: 6px; border-radius: 50%; flex-shrink: 0; display: inline-block; }
                    .ae-modal-dot-success { background: #10b981; }
                    .ae-modal-dot-danger  { background: #ef4444; }
                    .ae-btn-modal-primary {
                        background: #4f46e5 !important;
                        border-color: #4f46e5 !important;
                        font-size: 13px;
                        font-weight: 600;
                        color: #fff !important;
                    }
                    .ae-btn-modal-primary:hover { background: #4338ca !important; border-color: #4338ca !important; }
                    .ae-btn-modal-primary:disabled { opacity: 0.7; }
                    @keyframes ae-spin { to { transform: rotate(360deg); } }
                `}</style>

                {/* Scoped styles ------------------------------------------------ */}
                <style>{`
                    .ae-page {
                        --ae-border: ${isDark ? '#2c3242' : '#e6e8ec'};
                        --ae-bg-subtle: ${isDark ? '#1b2030' : '#f8f9fb'};
                        --ae-surface: ${isDark ? '#11151f' : '#ffffff'};
                        --ae-text: ${isDark ? '#e7e9ee' : '#1f2430'};
                        --ae-muted: ${isDark ? '#8a93a6' : '#6b7280'};
                        --ae-accent: #4f46e5;
                        --ae-mono: 'SFMono-Regular', 'Roboto Mono', Menlo, Consolas, monospace;
                        font-feature-settings: "tnum" 1;
                    }

                    .ae-crumb { font-size: 12px; color: var(--ae-muted); }
                    .ae-crumb b { color: var(--ae-text); font-weight: 600; }
                    .ae-page-title { font-size: 19px; font-weight: 700; letter-spacing: -0.01em; color: var(--ae-text); }
                    .ae-page-sub { font-size: 12.5px; color: var(--ae-muted); }

                    .ae-stat-bar {
                        display: flex;
                        border: 1px solid var(--ae-border);
                        border-radius: 10px;
                        background: var(--ae-surface);
                        overflow: hidden;
                    }
                    .ae-stat {
                        flex: 1;
                        min-width: 0;
                        padding: 12px 18px;
                        display: flex;
                        flex-direction: column;
                        gap: 4px;
                        border-right: 1px solid var(--ae-border);
                    }
                    .ae-stat:last-child { border-right: none; }
                    .ae-stat-label {
                        font-size: 10.5px;
                        text-transform: uppercase;
                        letter-spacing: 0.08em;
                        font-weight: 700;
                        color: var(--ae-muted);
                        display: flex; align-items: center; gap: 6px;
                    }
                    .ae-stat-value {
                        font-family: var(--ae-mono);
                        font-size: 22px;
                        font-weight: 700;
                        color: var(--ae-text);
                        line-height: 1.1;
                    }

                    .ae-card {
                        border: 1px solid var(--ae-border);
                        border-radius: 10px;
                        background: var(--ae-surface);
                        overflow: hidden;
                    }

                    .ae-toolbar {
                        display: flex; flex-wrap: wrap; gap: 8px; align-items: center;
                        padding: 10px 14px;
                        border-bottom: 1px solid var(--ae-border);
                        background: var(--ae-bg-subtle);
                    }
                    .ae-toolbar .form-control,
                    .ae-toolbar .form-select,
                    .ae-toolbar .input-group-text {
                        font-size: 12.5px;
                        height: 34px;
                        border-color: var(--ae-border);
                        background: var(--ae-surface);
                        color: var(--ae-text);
                    }
                    .ae-toolbar .form-select { padding-top: 0; padding-bottom: 0; }

                    .ae-table { margin-bottom: 0; font-size: 12.5px; }
                    .ae-table thead th {
                        font-size: 10.5px;
                        text-transform: uppercase;
                        letter-spacing: 0.07em;
                        font-weight: 700;
                        color: var(--ae-muted);
                        background: var(--ae-bg-subtle);
                        border-bottom: 1px solid var(--ae-border);
                        padding: 9px 14px;
                        white-space: nowrap;
                        position: sticky; top: 0; z-index: 1;
                    }
                    .ae-table tbody td {
                        padding: 9px 14px;
                        vertical-align: middle;
                        border-bottom: 1px solid var(--ae-border);
                        color: var(--ae-text);
                    }
                    .ae-table tbody tr:last-child td { border-bottom: none; }
                    .ae-table tbody tr { transition: background-color .1s ease; }
                    .ae-table tbody tr:hover { background: var(--ae-bg-subtle); }

                    .ae-mono { font-family: var(--ae-mono); font-size: 12px; color: var(--ae-muted); }
                    .ae-row-idx { font-family: var(--ae-mono); font-size: 11.5px; color: var(--ae-muted); }
                    .ae-ellipsis {
                        max-width: 220px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; display: inline-block;
                    }

                    .ae-badge {
                        display: inline-flex; align-items: center; gap: 5px;
                        padding: 2px 8px; border-radius: 4px;
                        font-size: 10.5px; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase;
                        white-space: nowrap;
                    }
                    .ae-badge-primary   { background: ${isDark ? '#1f2350' : '#eef0ff'}; color: ${isDark ? '#9aa3ff' : '#4338ca'}; }
                    .ae-badge-success   { background: ${isDark ? '#10301f' : '#ecfdf5'}; color: ${isDark ? '#5fd9a4' : '#047857'}; }
                    .ae-badge-info      { background: ${isDark ? '#102a3d' : '#eff6ff'}; color: ${isDark ? '#7cc1f5' : '#1d4ed8'}; }
                    .ae-badge-warning   { background: ${isDark ? '#3a2c10' : '#fffbeb'}; color: ${isDark ? '#f0c468' : '#b45309'}; }
                    .ae-badge-secondary { background: ${isDark ? '#262c3b' : '#f3f4f6'}; color: ${isDark ? '#aeb6c4' : '#4b5563'}; }
                    .ae-badge-danger    { background: ${isDark ? '#3a1414' : '#fef2f2'}; color: ${isDark ? '#f3a3a3' : '#b91c1c'}; }

                    .ae-status { display: inline-flex; align-items: center; gap: 6px; font-size: 12px; font-weight: 600; }
                    .ae-dot { width: 6px; height: 6px; border-radius: 50%; flex-shrink: 0; }
                    .ae-dot-success { background: #10b981; box-shadow: 0 0 0 3px ${isDark ? 'rgba(16,185,129,0.15)' : 'rgba(16,185,129,0.12)'}; }
                    .ae-dot-danger  { background: #ef4444; box-shadow: 0 0 0 3px ${isDark ? 'rgba(239,68,68,0.15)' : 'rgba(239,68,68,0.12)'}; }

                    .ae-badge-completed {
    background: #ecfdf5;
    color: #047857;
}

.ae-badge-pending {
    background: #fffbeb;
    color: #b45309;
}

.ae-badge-failed {
    background: #fef2f2;
    color: #b91c1c;
}

.ae-dark .ae-badge-completed {
    background: #10301f;
    color: #5fd9a4;
}

.ae-dark .ae-badge-pending {
    background: #3a2c10;
    color: #f0c468;
}

.ae-dark .ae-badge-failed {
    background: #3a1414;
    color: #f3a3a3;
}

                    .ae-icon-btn {
                        width: 28px; height: 28px;
                        display: inline-flex; align-items: center; justify-content: center;
                        border-radius: 6px;
                        border: 1px solid var(--ae-border);
                        background: var(--ae-surface);
                        color: var(--ae-muted);
                        transition: all .12s ease;
                    }
                    .ae-icon-btn:hover { color: var(--ae-text); border-color: var(--ae-accent); background: ${isDark ? '#1b1f33' : '#eef0ff'}; }
                    .ae-icon-btn-danger:hover { color: #dc2626; border-color: #fecaca; background: ${isDark ? '#2a1414' : '#fef2f2'}; }
                    .ae-icon-btn:disabled { opacity: .5; cursor: not-allowed; }

                    .ae-pagination { display: flex; align-items: center; gap: 6px; }
                    .ae-page-btn {
                        min-width: 28px; height: 28px; padding: 0 6px;
                        display: inline-flex; align-items: center; justify-content: center;
                        border-radius: 6px; font-size: 12px; font-weight: 600;
                        border: 1px solid var(--ae-border);
                        background: var(--ae-surface); color: var(--ae-text);
                        transition: all .12s ease;
                    }
                    .ae-page-btn:hover:not(:disabled) { border-color: var(--ae-accent); }
                    .ae-page-btn:disabled { opacity: .4; }
                    .ae-page-btn.active { background: var(--ae-accent); border-color: var(--ae-accent); color: #fff; }

                    .ae-section { margin-bottom: 22px; }
                    .ae-section-title {
                        font-size: 10.5px; text-transform: uppercase; letter-spacing: 0.09em; font-weight: 700;
                        color: var(--ae-muted);
                        padding-bottom: 8px; margin-bottom: 14px;
                        border-bottom: 1px solid var(--ae-border);
                    }
                    .ae-field-label {
                        font-size: 12.5px; font-weight: 600; color: var(--ae-text);
                        display: flex; align-items: center; gap: 6px; margin-bottom: 6px;
                    }
                    .ae-field-hint { font-size: 11px; color: var(--ae-muted); margin-top: 5px; }
                    .ae-page .form-control,
                    .ae-page .form-select {
                        font-size: 13px; border-radius: 6px; border-color: var(--ae-border);
                        background: var(--ae-surface); color: var(--ae-text);
                    }
                    .ae-page .form-control:focus,
                    .ae-page .form-select:focus {
                        border-color: var(--ae-accent); box-shadow: 0 0 0 3px rgba(79,70,229,0.12);
                    }
                    .ae-page .react-datepicker-wrapper { width: 100%; }

                    .ae-btn-primary {
                        background: var(--ae-accent); border-color: var(--ae-accent);
                        font-size: 13px; font-weight: 600;
                    }
                    .ae-btn-primary:hover { background: #4338ca; border-color: #4338ca; }

                `}</style>

                {/* Page Header */}
                <div className="ae-card mb-3" style={{ padding: '14px 18px' }}>
                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                        <div className='w-50'>
                            <div className="d-flex align-items-center gap-2 mb-1">
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    className="d-flex align-items-center gap-1 px-2 py-1 w-25 justify-content-center"
                                    style={{ fontSize: '12px' }}
                                    onClick={() => navigate('/events')}
                                >
                                    <ArrowLeft size={13} /> Back
                                </Button>
                                {/* <span className="ae-crumb">All events / <b>Automation events</b></span> */}
                            </div>
                            <h4 className="ae-page-title mb-1">Automation Events</h4>
                            <p className="ae-page-sub mb-0">Scheduled activate / deactivate actions.</p>
                        </div>
                        <Button
                            className="ae-btn-primary d-flex align-items-center gap-2 justify-content-center w-25"
                            onClick={openAddModal}
                        >
                            <PlusLg size={15} /> Add event
                        </Button>
                    </div>
                </div>

                {/* Summary stat strip */}
                {/* <div className="ae-stat-bar mb-3">
                    <div className="ae-stat">
                        <span className="ae-stat-label">Total records</span>
                        <span className="ae-stat-value">{pagination.totalRecords}</span>
                    </div>
                    <div className="ae-stat">
                        <span className="ae-stat-label"><span className="ae-dot ae-dot-success" />Active</span>
                        <span className="ae-stat-value">{totalActive}</span>
                    </div>
                    <div className="ae-stat">
                        <span className="ae-stat-label"><span className="ae-dot ae-dot-danger" />Inactive</span>
                        <span className="ae-stat-value">{totalInactive}</span>
                    </div>
                    <div className="ae-stat">
                        <span className="ae-stat-label">Page</span>
                        <span className="ae-stat-value">{pagination.currentPage}<span style={{ fontSize: '13px', color: 'var(--ae-muted)', fontWeight: 600 }}> / {pagination.totalPages}</span></span>
                    </div>
                </div> */}

                {/* Table Card */}
                <div className="ae-card">

                    {/* Filters */}
                    <div className="ae-toolbar">
                        <InputGroup style={{ maxWidth: '240px' }}>
                            <InputGroup.Text className="border-end-0">
                                <Search size={13} />
                            </InputGroup.Text>
                            <Form.Control
                                placeholder="Search by event type…"
                                style={{ borderLeft: 'none' }}
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                            />
                        </InputGroup>
                        <InputGroup style={{ width: 'auto' }}>
                            <InputGroup.Text className="border-end-0">
                                <Funnel size={12} />
                            </InputGroup.Text>
                            <Form.Select
                                style={{ width: '180px', borderLeft: 'none' }}
                                value={filterType}
                                onChange={e => { setFilterType(e.target.value); setPage(1); }}
                            >
                                <option value="">All Event types</option>
                                {EVENT_NAME_OPTIONS.map(o => (
                                    <option key={o.value} value={o.value}>{o.label}</option>
                                ))}
                            </Form.Select>
                        </InputGroup>
                        <Form.Select
                            style={{ width: '140px' }}
                            value={filterStatus}
                            onChange={e => { setFilterStatus(e.target.value); setPage(1); }}
                        >
                            <option value="">All Event status</option>
                            <option value="ACTIVE">Active</option>
                            <option value="INACTIVE">Inactive</option>
                        </Form.Select>

                        {(search || filterType || filterStatus) && (
                            <Button
                                variant="link"
                                className="p-0 ms-auto"
                                style={{ fontSize: '12px', textDecoration: 'none' }}
                                onClick={() => { setSearch(''); setFilterType(''); setFilterStatus(''); setPage(1); }}
                            >
                                Clear filters
                            </Button>
                        )}
                    </div>

                    {/* Loading */}
                    {loading && (
                        <div className="text-center py-5">
                            <Loader2 size={20} className="ae-mono" style={{ animation: 'spin 0.8s linear infinite', color: 'var(--ae-accent)' }} />
                            <p className="ae-page-sub mt-2 mb-0">Loading events…</p>
                            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                        </div>
                    )}

                    {/* Error */}
                    {!loading && error && (
                        <div className="d-flex flex-column align-items-center py-5 gap-2" style={{ color: '#dc2626' }}>
                            <AlertCircle size={26} />
                            <p className="mb-1" style={{ fontSize: '13px' }}>{error}</p>
                            <Button size="sm" variant="outline-danger" onClick={() => fetchEvents(page)}>Retry</Button>
                        </div>
                    )}

                    {/* Empty */}
                    {!loading && !error && filteredEvents.length === 0 && (
                        <div className="text-center py-5">
                            <CalendarEvent size={32} style={{ color: 'var(--ae-muted)' }} className="mb-2" />
                            <p className="ae-page-sub mb-0">No automation events match these filters</p>
                        </div>
                    )}

                    {/* Table */}
                    {!loading && !error && filteredEvents.length > 0 && (
                        <div style={{ overflowX: 'auto' }}>
                            <Table responsive hover className="ae-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: '44px' }}>Sr No</th>
                                        <th>Event type</th>
                                        <th>Applicable to</th>
                                        <th>Event Status</th>
                                        <th>Date</th>
                                        <th>Time</th>
                                        <th>Execution Status</th>
                                        <th style={{ width: '90px' }} className="text-end">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredEvents.map((ev, idx) => (
                                        <tr key={ev._id}>
                                            <td className="ae-row-idx">
                                                {String((pagination.currentPage - 1) * pagination.limit + idx + 1).padStart(2, '0')}
                                            </td>
                                            <td>
                                                <span className={`ae-badge ae-badge-${EVENT_TYPE_COLORS[ev.eventName] || 'secondary'}`}>
                                                    {EVENT_NAME_OPTIONS.find(o => o.value === ev.eventName)?.label || safe(ev.eventName)}
                                                </span>
                                            </td>
                                            <td>
                                                <span className="ae-ellipsis" title={getApplicableName(ev)}>
                                                    {getApplicableName(ev)}
                                                </span>
                                            </td>
                                            <td>
                                                <span className="ae-status">
                                                    <span className={`ae-dot ${ev.eventStatus === 'ACTIVE' ? 'ae-dot-success' : 'ae-dot-danger'}`} />
                                                    {safe(ev.eventStatus)}
                                                </span>
                                            </td>
                                            <td className="ae-mono">
                                                {ev.eventDate
                                                    ? new Date(ev.eventDate).toLocaleDateString('en-IN', {
                                                        day: '2-digit', month: 'short', year: 'numeric',
                                                    })
                                                    : '—'}
                                            </td>
                                            <td className="ae-mono">{safe(ev.eventTime)}</td>

                                            <td className="ae-mono">
                                                {/* {ev.eventExecTime
                                                    ? new Date(ev.eventExecTime).toLocaleString('en-IN')
                                                    : '—'} */}
                                                <td>
                                                    <span
                                                        className={`ae-badge ${ev.status === 'COMPLETED'
                                                                ? 'ae-badge-completed'
                                                                : ev.status === 'FAILED'
                                                                    ? 'ae-badge-failed'
                                                                    : 'ae-badge-pending'
                                                            }`}
                                                    >
                                                        {safe(ev.status)}
                                                    </span>
                                                </td>
                                            </td>
                                            <td>
                                                <div className="d-flex gap-2 justify-content-end">
                                                    {/* EDIT: Edit button commented out */}
                                                    {/* <button
                                                        className="ae-icon-btn"
                                                        onClick={() => openEditModal(ev)}
                                                        title="Edit"
                                                    >
                                                        <PencilSquare size={13} />
                                                    </button> */}
                                                    <button
                                                        className="ae-icon-btn ae-icon-btn-danger"
                                                        disabled={deleting === ev._id}
                                                        onClick={() => openDeleteModal(ev._id)}
                                                        title="Delete"
                                                    >
                                                        {deleting === ev._id
                                                            ? <Loader2 size={13} style={{ animation: 'spin 0.8s linear infinite' }} />
                                                            : <Trash3 size={13} />}
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    )}

                    {/* Pagination */}
                    {!loading && !error && pagination.totalPages > 1 && (
                        <div className="d-flex align-items-center justify-content-between px-3 py-2" style={{ borderTop: '1px solid var(--ae-border)' }}>
                            <p className="ae-page-sub mb-0">
                                Page {pagination.currentPage} of {pagination.totalPages} · {pagination.totalRecords} total records
                            </p>
                            <div className="ae-pagination">
                                <button
                                    className="ae-page-btn"
                                    disabled={page === 1}
                                    onClick={() => setPage(prev => prev - 1)}
                                >
                                    <ChevronLeft size={13} />
                                </button>
                                {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map(p => (
                                    <button
                                        key={p}
                                        className={`ae-page-btn ${p === page ? 'active' : ''}`}
                                        onClick={() => setPage(p)}
                                    >
                                        {p}
                                    </button>
                                ))}
                                <button
                                    className="ae-page-btn"
                                    disabled={page === pagination.totalPages}
                                    onClick={() => setPage(prev => prev + 1)}
                                >
                                    <ChevronRight size={13} />
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* ── Add Modal ────────────────────────────────────────────── */}
                <Modal
                    show={showModal}
                    onHide={() => setShowModal(false)}
                    centered
                    size="lg"
                    className={`ae-modal ${isDark ? 'ae-dark' : ''}`}
                >
                    <Modal.Header closeButton>
                        <div>
                            <Modal.Title>Add Automation Event</Modal.Title>
                            <span className="ae-modal-subtitle">
                                Schedule an automatic activate / deactivate action.
                            </span>
                        </div>
                    </Modal.Header>

                    <Modal.Body>
                        {formError && (
                            <div className="ae-modal-error-box">
                                <AlertCircle size={15} style={{ marginTop: '1px', flexShrink: 0 }} />
                                <span>{formError}</span>
                            </div>
                        )}

                        <div className="ae-modal-grid">

                            {/* Event type — full width */}
                            <Form.Group className="ae-full-col">
                                <Form.Label className="ae-modal-field-label">
                                    <Tag size={13} /> Event type <span className="text-danger">*</span>
                                </Form.Label>
                                <Form.Select
                                    name="eventName"
                                    value={form.eventName}
                                    onChange={handleFormChange}
                                >
                                    {EVENT_NAME_OPTIONS.map(o => (
                                        <option key={o.value} value={o.value}>{o.label}</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>

                            {/* Applicable entity — full width */}
                            <Form.Group className="ae-full-col">
                                {applicableLabel !== 'Outlet' && (
                                    <Form.Label className="ae-modal-field-label">
                                        <ShopWindow size={13} /> {applicableLabel} <span className="text-danger">*</span>
                                    </Form.Label>
                                )}

                                {form.eventName === 'OUTLET_ACTION' ? (
                                    <OutletSelect
                                        value={form.applicableOutlet}
                                        onChange={(option) => handleSelectChange('applicableOutlet', option)}
                                    />
                                ) : (
                                    <Select
                                        options={selectOptionsMap[form.eventName] || []}
                                        value={form[applicableKey]}
                                        onChange={(option) => handleSelectChange(applicableKey, option)}
                                        placeholder={`Select ${applicableLabel.toLowerCase()}…`}
                                        isClearable
                                        isSearchable
                                        styles={selectStyles}
                                        menuPlacement="auto" // or "top"
                                        menuPosition="fixed"
                                        menuPortalTarget={document.body}
                                    />
                                )}
                            </Form.Group>

                            {/* Event date */}
                            <Form.Group>
                                <Form.Label className="ae-modal-field-label">
                                    <CalendarEvent size={13} /> Event date <span className="text-danger">*</span>
                                </Form.Label>
                                <DatePicker
                                    selected={form.eventDate}
                                    onChange={(date) => setForm(prev => ({ ...prev, eventDate: date }))}
                                    dateFormat="dd/MM/yyyy"
                                    placeholderText="Select date"
                                    minDate={new Date()}
                                    className="form-control"
                                    wrapperClassName="w-100"
                                />
                            </Form.Group>

                            {/* Event time */}
                            <Form.Group>
                                <Form.Label className="ae-modal-field-label">
                                    <Clock size={13} /> Event time <span className="text-muted fw-normal">(optional)</span>
                                </Form.Label>
                                <Form.Control
                                    type="time"
                                    name="eventTime"
                                    value={form.eventTime}
                                    onChange={handleFormChange}
                                />

                            </Form.Group>

                            {/* Event status — full width */}
                            <Form.Group className="ae-full-col">
                                <Form.Label className="ae-modal-field-label">
                                    <span className={`ae-modal-dot ${form.eventStatus === 'ACTIVE' ? 'ae-modal-dot-success' : 'ae-modal-dot-danger'}`} />
                                    Event Status
                                </Form.Label>
                                <div className="ae-modal-status-toggle">
                                    <button
                                        type="button"
                                        className={`ae-modal-status-btn ${form.eventStatus === 'ACTIVE' ? 'is-active' : ''}`}
                                        onClick={() => setForm(prev => ({ ...prev, eventStatus: 'ACTIVE' }))}
                                    >
                                        <span className="ae-modal-dot ae-modal-dot-success" /> Active
                                    </button>
                                    <button
                                        type="button"
                                        className={`ae-modal-status-btn ${form.eventStatus === 'INACTIVE' ? 'is-inactive' : ''}`}
                                        onClick={() => setForm(prev => ({ ...prev, eventStatus: 'INACTIVE' }))}
                                    >
                                        <span className="ae-modal-dot ae-modal-dot-danger" /> Inactive
                                    </button>
                                </div>
                                {/* <p className="ae-modal-field-hint">
                                    Inactive events are saved but won't trigger on schedule.
                                </p> */}
                            </Form.Group>

                        </div>
                    </Modal.Body>

                    <Modal.Footer>
                        <Button
                            variant="outline-secondary"
                            style={{ fontSize: '13px' }}
                            onClick={() => setShowModal(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            className="ae-btn-modal-primary"
                            onClick={handleSave}
                            disabled={saving}
                        >
                            {saving
                                ? <><Loader2 size={14} className="me-2" style={{ animation: 'ae-spin 0.8s linear infinite', display: 'inline-block' }} />Saving…</>
                                : 'Create event'}
                        </Button>
                    </Modal.Footer>
                </Modal>

                {/* ── Delete confirmation modal ───────────────────────────── */}
                <Modal
                    show={showDeleteModal}
                    onHide={closeDeleteModal}
                    centered
                    className={`ae-modal ${isDark ? 'ae-dark' : ''}`}
                >
                    <Modal.Header closeButton>
                        <Modal.Title>Delete automation event</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <div className="d-flex gap-3 align-items-start">
                            <div style={{
                                width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                                background: isDark ? '#3a1414' : '#fef2f2',
                                color: isDark ? '#f3a3a3' : '#b91c1c',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                                <AlertCircle size={18} />
                            </div>
                            <div>
                                <p className="mb-1" style={{ fontSize: '13.5px', fontWeight: 600, color: 'var(--ae-text)' }}>
                                    Are you sure you want to delete this event?
                                </p>
                                <p className="mb-0" style={{ fontSize: '12.5px', color: 'var(--ae-muted)' }}>
                                    This action cannot be undone.
                                </p>
                            </div>
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button
                            variant="outline-secondary"
                            style={{ fontSize: '13px' }}
                            onClick={closeDeleteModal}
                            disabled={deleting === deleteTargetId}
                        >
                            Cancel
                        </Button>
                        <Button
                            style={{ fontSize: '13px', fontWeight: 600 }}
                            variant="danger"
                            onClick={confirmDelete}
                            disabled={deleting === deleteTargetId}
                        >
                            {deleting === deleteTargetId
                                ? <><Loader2 size={14} className="me-2" style={{ animation: 'ae-spin 0.8s linear infinite', display: 'inline-block' }} />Deleting…</>
                                : 'Delete event'}
                        </Button>
                    </Modal.Footer>
                </Modal>
            </div>
        </React.Fragment>
    );
};

export default AutomationEvents;